/**
 * Advanced Question Bank: Domain 2 — Quantitative Reasoning (التفكير الكمي والرياضي)
 * 15 Psychometrically Rigorous Generative Template Families (Mathematical Induction / Gq)
 */

export const ADVANCED_QUANTITATIVE_TEMPLATES = [
  // 1. Interleaved Dual-Rule Series
  {
    id: 'quant_arithmetic_interleaved',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Series: A1, B1, A2, B2, A3, B3, [A4]
      // A increases by stepA (+3 or +4), B multiplies by multB (*2 or +5)
      const startA = prng.int(4, 12);
      const stepA = prng.pick([3, 4, 5]);
      const startB = prng.int(20, 40);
      const stepB = prng.pick([-3, -4, -5]);

      const aVals = [startA, startA + stepA, startA + 2 * stepA, startA + 3 * stepA];
      const bVals = [startB, startB + stepB, startB + 2 * stepB];

      const sequence = [aVals[0], bVals[0], aVals[1], bVals[1], aVals[2], bVals[2], '?'];
      const correctVal = aVals[3];

      const distractors = [correctVal - stepA, correctVal + stepA, correctVal + 1];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو العدد الذي يجب أن يحل محل علامة الاستفهام لإكمال المتتالية الرقمية المزدوجة؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `Which number logically replaces the question mark to complete the interleaved number series?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `تتكون المتتالية من سلسلتين متداخلتين: السلسلة الأولى عند الحدود الفردية تزداد بمقدار ${stepA} (${aVals[0]}، ${aVals[1]}، ${aVals[2]}، ${correctVal})، بينما السلسلة الثانية عند الحدود الزوجية تتناقص بمقدار ${Math.abs(stepB)}. الحد المستهدف هو الحد الفردي الرابع، وقيمته ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The sequence interleaves two distinct progressions: odd indices increase by ${stepA} (${aVals[0]}, ${aVals[1]}, ${aVals[2]}, ${correctVal}), while even indices decrease by ${Math.abs(stepB)}. The missing 7th term is odd, resolving to ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 2. Modular Clock Arithmetic
  {
    id: 'quant_modular_arithmetic_clock',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const mod = 12;
      const start = prng.int(1, 11);
      const multiplier = prng.pick([3, 5, 7]); // coprime to 12

      // e.g. x_n = (start + n * multiplier) % 12, converted to 1..12 clock face
      const toClock = (v) => ((v % mod) === 0 ? 12 : (v % mod));

      const t1 = toClock(start);
      const t2 = toClock(start + multiplier);
      const t3 = toClock(start + 2 * multiplier);
      const t4 = toClock(start + 3 * multiplier);
      const correctVal = toClock(start + 4 * multiplier);

      const sequence = [t1, t2, t3, t4, '?'];
      const distractors = [toClock(correctVal + 1), toClock(correctVal + 2), toClock(correctVal - 1)];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في نظام حسابي دوري لساعة مكونة من 12 ساعة، ما هو الرقم التالي في المتتالية؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `In a 12-hour modulo cyclic system, which value completes the sequence?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `تتقدم الأرقام بإضافة ${multiplier} ساعات في كل خطوة مع الالتفاف حول دورة الـ 12 ساعة. بإضافة ${multiplier} إلى ${t4} نصل إلى ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Values advance by ${multiplier} modulo 12 per iteration. Adding ${multiplier} to ${t4} wraps cyclically to ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 3. 3x3 Magic Numeric Grid Sum Balance
  {
    id: 'quant_matrix_row_sum',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const targetSum = prng.pick([30, 36, 42]);
      // Generate 3x3 grid where every row sums to targetSum
      const genRow = () => {
        const a = prng.int(5, Math.floor(targetSum / 3));
        const b = prng.int(5, Math.floor((targetSum - a) / 2));
        const c = targetSum - a - b;
        return [a, b, c];
      };

      const r1 = genRow();
      const r2 = genRow();
      const r3 = genRow();

      const correctVal = r3[2];
      const distractors = [correctVal - 2, correctVal + 2, correctVal + 4];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const gridText = `|  ${r1[0]}  |  ${r1[1]}  |  ${r1[2]}  |\n|  ${r2[0]}  |  ${r2[1]}  |  ${r2[2]}  |\n|  ${r3[0]}  |  ${r3[1]}  |   ?   |`;

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تحقق صفوف المصفوفة الرقمية التالية توازنًا حسابيًا بمجموع أفقي ثابت لكل صف. ما هو العدد المفقود؟\n\n${gridText}`,
        questionText_en: `Rows in the numeric matrix maintain an identical horizontal sum balance. Which number replaces the question mark?\n\n${gridText}`,
        options,
        correctIndex,
        explanation_ar: `مجموع عناصر الصف الأول = ${r1[0]} + ${r1[1]} + ${r1[2]} = ${targetSum}. بالتالي، العدد المفقود في الصف الثالث هو: ${targetSum} - (${r3[0]} + ${r3[1]}) = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Row 1 sum equals ${r1[0]} + ${r1[1]} + ${r1[2]} = ${targetSum}. Evaluating Row 3 yields: ${targetSum} - (${r3[0]} + ${r3[1]}) = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 4. Generalized Fibonacci Recurrence (T_n = a*T_{n-1} + b)
  {
    id: 'quant_fibonacci_generalized',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const mult = prng.pick([2, 3]);
      const shift = prng.pick([1, -1, 2]);
      const t1 = prng.int(2, 5);
      const t2 = t1 * mult + shift;
      const t3 = t2 * mult + shift;
      const t4 = t3 * mult + shift;
      const correctVal = t4 * mult + shift;

      const sequence = [t1, t2, t3, t4, '?'];
      const rawDist = [correctVal - mult, correctVal + mult, correctVal + mult * 2, correctVal - mult * 2 + 1];
      const distractors = Array.from(new Set(rawDist.filter(v => v !== correctVal))).slice(0, 3);
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `اكتشف القاعدة الحسابية المتكررة وأوجد العدد التالي في المتتالية:\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `Deduce the recursive operational rule to determine the missing number:\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `القاعدة الحسابية هي: ضرب الحد السابق في ${mult} ثم ${shift >= 0 ? `إضافة ${shift}` : `طرح ${Math.abs(shift)}`}. بتطبيق القاعدة على ${t4}: (${t4} × ${mult}) ${shift >= 0 ? `+ ${shift}` : `- ${Math.abs(shift)}`} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The recurrence relation is T(n) = ${mult} * T(n-1) ${shift >= 0 ? `+ ${shift}` : `- ${Math.abs(shift)}`}. Applying this to ${t4} gives ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 5. Symbolic Operator Deduction
  {
    id: 'quant_nested_operator_puzzle',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Rule: a ⊗ b = a * multA + b * multB
      const multA = prng.pick([2, 3]);
      const multB = prng.pick([3, 4]);

      const ex1_a = prng.int(2, 4);
      const ex1_b = prng.int(3, 5);
      const ex1_res = ex1_a * multA + ex1_b * multB;

      const ex2_a = prng.int(5, 7);
      const ex2_b = prng.int(2, 4);
      const ex2_res = ex2_a * multA + ex2_b * multB;

      const target_a = prng.int(4, 6);
      const target_b = prng.int(6, 8);
      const correctVal = target_a * multA + target_b * multB;

      const distractors = [correctVal - multA, correctVal + multB, correctVal + 2];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت العملية الرمزية ⊗ تحقق العلاقات التالية:\n\n• ${ex1_a} ⊗ ${ex1_b} = ${ex1_res}\n• ${ex2_a} ⊗ ${ex2_b} = ${ex2_res}\n\nفما هي قيمة: ${target_a} ⊗ ${target_b} ؟`,
        questionText_en: `If the custom operator ⊗ satisfies:\n\n• ${ex1_a} ⊗ ${ex1_b} = ${ex1_res}\n• ${ex2_a} ⊗ ${ex2_b} = ${ex2_res}\n\nWhat is the value of: ${target_a} ⊗ ${target_b} ?`,
        options,
        correctIndex,
        explanation_ar: `القاعدة المشفرة للعملية هي: (العدد الأول × ${multA}) + (العدد الثاني × ${multB}). بالتعويض: (${target_a} × ${multA}) + (${target_b} × ${multB}) = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The underlying operator definition is a ⊗ b = (${multA} * a) + (${multB} * b). Computing for (${target_a}, ${target_b}) yields ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 6. Exponential Power Series Progression (2^k - 1)
  {
    id: 'quant_exponential_decay_growth',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const base = prng.pick([2, 3]);
      const offset = prng.pick([1, -1]);

      const t1 = Math.pow(base, 1) + offset;
      const t2 = Math.pow(base, 2) + offset;
      const t3 = Math.pow(base, 3) + offset;
      const t4 = Math.pow(base, 4) + offset;
      const correctVal = Math.pow(base, 5) + offset;

      const sequence = [t1, t2, t3, t4, '?'];
      const rawDist = [correctVal - base, correctVal + base, correctVal + base * 2, correctVal - base * 2];
      const distractors = Array.from(new Set(rawDist.filter(v => v !== correctVal))).slice(0, 3);
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو العدد الذي يكمل متتالية القوى الأسية التالية؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `Which number completes the exponential power series?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `صيغة الحدود تتبع المعادلة الأسية (${base}^n ${offset >= 0 ? `+ ${offset}` : `- ${Math.abs(offset)}`}). عند الحد الخامس (n=5): ${base}^5 ${offset >= 0 ? `+ ${offset}` : `- ${Math.abs(offset)}`} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Terms follow the closed form (${base}^n ${offset >= 0 ? `+ ${offset}` : `- ${Math.abs(offset)}`}). For n=5, evaluating yields ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 7. Semiprime Factorization Progression
  {
    id: 'quant_prime_factorization_rank',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const primes = [2, 3, 5, 7, 11, 13, 17];
      const fixedPrime = prng.pick([3, 5]);
      // Sequence: fixedPrime * primes[0], fixedPrime * primes[1], ...
      const t1 = fixedPrime * primes[0];
      const t2 = fixedPrime * primes[1];
      const t3 = fixedPrime * primes[2];
      const t4 = fixedPrime * primes[3];
      const correctVal = fixedPrime * primes[4];

      const sequence = [t1, t2, t3, t4, '?'];
      const distractors = [correctVal - fixedPrime, correctVal + fixedPrime, correctVal + 2];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تنتج متتالية الأعداد شبه الأولية التالية من ضرب عدد أولي ثابت في متتالية الأعداد الأولية المتتالية. ما هو العدد الناقص؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `The following semiprime sequence is formed by multiplying a constant prime by consecutive prime numbers. Which number completes it?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `الحدود تمثل حاصل ضرب العدد الأولي ${fixedPrime} في الأعداد الأولية المتتالية (2، 3، 5، 7، 11). الحد الخامس هو ${fixedPrime} × 11 = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Each term multiplies prime ${fixedPrime} by the sequential primes (2, 3, 5, 7, 11). The 5th term is ${fixedPrime} * 11 = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 8. Figurate Triangular Numbers Expansion
  {
    id: 'quant_triangular_numbers',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Triangular numbers: T_n = n(n+1)/2
      const offset = prng.int(1, 3);
      const t1 = (1 * 2) / 2 + offset;
      const t2 = (2 * 3) / 2 + offset;
      const t3 = (3 * 4) / 2 + offset;
      const t4 = (4 * 5) / 2 + offset;
      const correctVal = (5 * 6) / 2 + offset;

      const sequence = [t1, t2, t3, t4, '?'];
      const distractors = [correctVal - 2, correctVal + 2, correctVal + 5];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتبع الحدود نمط الأعداد المثلثية مضافًا إليها إزاحة ثابتة. ما هو العدد التالي في المتتالية؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `The series follows triangular numbers augmented by a fixed constant. Which number follows next?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `الأعداد المثلثية هي (1، 3، 6، 10، 15). بإضافة الإزاحة (${offset}) إلى العدد المثلثي الخامس (15) نحصل على: 15 + ${offset} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Standard triangular numbers (1, 3, 6, 10, 15) shifted by ${offset} produce a fifth term of 15 + ${offset} = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 9. Harmonic Mean Rate Problem
  {
    id: 'quant_harmonic_mean_rate',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Two workers complete a job in A hours and B hours. Together: (A*B)/(A+B)
      // We pick nice numbers: A=6, B=3 -> 18/9 = 2. A=12, B=4 -> 48/16 = 3. A=20, B=5 -> 100/25 = 4
      const pairs = [
        { a: 6, b: 3, res: 2 },
        { a: 12, b: 4, res: 3 },
        { a: 20, b: 5, res: 4 },
        { a: 30, b: 6, res: 5 }
      ];
      const chosen = prng.pick(pairs);

      const correctVal = chosen.res;
      const distractors = [correctVal + 1, correctVal + 2, Math.max(1, correctVal - 1)];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${v} ساعات`,
        text_en: `${v} hours`,
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `يستطيع العامل الأول إنجاز مهمة كاملة بمفرده في ${chosen.a} ساعات، بينما ينجزها العامل الثاني بمفرده في ${chosen.b} ساعات. إذا عملا معًا بنفس الكفاءة المتزامنة، فكم ساعة تلزمهما لإنهاء المهمة؟`,
        questionText_en: `Worker A can finish a complete project in ${chosen.a} hours, while Worker B finishes it alone in ${chosen.b} hours. Working concurrently at constant rates, how many hours will they take to complete it together?`,
        options,
        correctIndex,
        explanation_ar: `معدل العمل المشترك = (1 / ${chosen.a}) + (1 / ${chosen.b}) = (${chosen.a + chosen.b}) / (${chosen.a * chosen.b}). وبالتالي فإن الزمن اللازم هو حاصل الضرب مقسومًا على حاصل الجمع: (${chosen.a} × ${chosen.b}) ÷ (${chosen.a} + ${chosen.b}) = ${correctVal} ساعات (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Combined rate is 1/A + 1/B = (A + B) / (A * B). The total time equals the reciprocal (A * B) / (A + B) = ${correctVal} hours (${options[correctIndex].id}).`
      };
    }
  },

  // 10. Differences of Consecutive Squares (2n - 1)
  {
    id: 'quant_consecutive_squares_diff',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Differences of squares: (n+1)^2 - n^2 = 2n + 1 (odd numbers)
      const startN = prng.int(3, 6);
      const s1 = Math.pow(startN, 2);
      const s2 = Math.pow(startN + 1, 2);
      const s3 = Math.pow(startN + 2, 2);
      const s4 = Math.pow(startN + 3, 2);
      const correctVal = Math.pow(startN + 4, 2);

      const sequence = [s1, s2, s3, s4, '?'];
      const distractors = [correctVal - (2 * (startN + 4) - 1), correctVal + 10, correctVal + 15];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تزداد الفروق بين حدود المتتالية التالية بمقدار فردي متتابع. ما هو المربع الكامل التالي في السلسلة؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `The increments between consecutive terms expand as sequential odd numbers. Which perfect square completes the series?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `الأعداد تمثل مربعات أعداد صحيحة متتالية: (${startN}²، ${startN + 1}²، ${startN + 2}²، ${startN + 3}²). الحد التالي هو مربع (${startN + 4})² = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The terms represent consecutive integers squared from ${startN}² to (${startN + 3})². The terminal term is (${startN + 4})² = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 11. Combinatorial Grid Path Counting
  {
    id: 'quant_combinatorial_path_count',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Paths from (0,0) to (w, h) taking only Right and Up: (w+h)! / (w! * h!)
      const cases = [
        { w: 2, h: 2, ans: 6 },
        { w: 3, h: 2, ans: 10 },
        { w: 3, h: 3, ans: 20 },
        { w: 4, h: 2, ans: 15 }
      ];
      const chosen = prng.pick(cases);

      const correctVal = chosen.ans;
      const distractors = [correctVal - 2, correctVal + 4, correctVal + 2];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${v} مسارات`,
        text_en: `${v} paths`,
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في شبكة شوارع منتظمة بأبعاد ${chosen.w} مربعات عرضًا و ${chosen.h} مربعات طولاً، كم عدد المسارات الفريدة الممكنة للانتقال من الزاوية الجنوبية الغربية إلى الزاوية الشمالية الشرقية بالتحرك شرقًا وشمالاً فقط دون تراجع؟`,
        questionText_en: `In a regular grid measuring ${chosen.w} blocks across and ${chosen.h} blocks up, how many unique shortest paths exist from the southwest corner to the northeast corner moving exclusively east and north?`,
        options,
        correctIndex,
        explanation_ar: `يحسب عدد المسارات بالتوافيق الرياضية: (${chosen.w} + ${chosen.h})! ÷ (${chosen.w}! × ${chosen.h}!). الناتج الدقيق هو ${correctVal} مسارًا فريدًا (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Path combinations evaluate via the binomial coefficient (${chosen.w} + ${chosen.h})! / (${chosen.w}! * ${chosen.h}!) = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 12. Digital Root / Sum of Digits Recurrence
  {
    id: 'quant_digit_sum_recurrence',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Series where term T_n = T_{n-1} + sumOfDigits(T_{n-1})
      const sumDigits = (n) => String(n).split('').reduce((a, b) => a + Number(b), 0);

      const start = prng.pick([12, 15, 21]);
      const t1 = start;
      const t2 = t1 + sumDigits(t1);
      const t3 = t2 + sumDigits(t2);
      const t4 = t3 + sumDigits(t3);
      const correctVal = t4 + sumDigits(t4);

      const sequence = [t1, t2, t3, t4, '?'];
      const distractors = [correctVal - 2, correctVal + 4, correctVal - sumDigits(t4)];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ينتج كل حد في المتتالية التالية من جمع الحد السابق مع مجموع خاناته الرقمية. ما هو العدد المفقود؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `Each consecutive term is generated by adding the previous number to the sum of its own individual digits. Which number replaces the question mark?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `الحد الرابع هو ${t4}، ومجموع خاناته = ${sumDigits(t4)}. وبالتالي: ${t4} + ${sumDigits(t4)} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The preceding term is ${t4}, whose digit sum equals ${sumDigits(t4)}. Computing ${t4} + ${sumDigits(t4)} gives ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 13. Determinant Product Ratio Balance in 2x2 Matrix
  {
    id: 'quant_matrix_determinant_balance',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 2x2 matrix with constant determinant k: a*d - b*c = k
      const k = prng.pick([2, 5, 10]);
      const a = prng.int(3, 6);
      const d = prng.int(4, 7);
      const prod = a * d - k;
      // Factor prod into b * c
      const factors = [];
      for (let f = 1; f <= prod; f++) {
        if (prod % f === 0) factors.push(f);
      }
      const b = prng.pick(factors);
      const c = prod / b;

      const matrixText = `|  ${a}   ${b}  |\n|  ${c}   ?  |   (المحدد = ${k})`;
      const correctVal = d;
      const distractors = [correctVal + 1, correctVal - 1, correctVal + 2];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت قيمة محدد المصفوفة الرياضية 2×2 التالية تساوي (${k})، فما هي قيمة المجهول في القطر الرئيسي؟\n\n${matrixText}`,
        questionText_en: `If the determinant of the following 2x2 matrix evaluates to (${k}), what is the missing value on the main diagonal?\n\n${matrixText}`,
        options,
        correctIndex,
        explanation_ar: `قانون المحدد: (القطر الرئيسي) - (القطر الفرعي) = (${a} × ?) - (${b} × ${c}) = ${k}. بالتالي: (${a} × ?) - ${b * c} = ${k}، إذن ? = (${k} + ${b * c}) ÷ ${a} = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Evaluating determinant |A| = (a * d) - (b * c) = ${k} yields d = (${k} + ${b * c}) / ${a} = ${correctVal} (${options[correctIndex].id}).`
      };
    }
  },

  // 14. Weighted Mixture Solution Balance
  {
    id: 'quant_weighted_average_mixture',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Mixture: V1 liters at C1% + V2 liters at C2% = (V1*C1 + V2*C2) / (V1+V2)
      // Pick clean integers: V1=10 at 20%, V2=30 at 40% -> (200 + 1200) / 40 = 1400/40 = 35%
      const cases = [
        { v1: 10, c1: 20, v2: 30, c2: 40, ans: 35 },
        { v1: 20, c1: 10, v2: 20, c2: 30, ans: 20 },
        { v1: 15, c1: 10, v2: 45, c2: 50, ans: 40 },
        { v1: 25, c1: 20, v2: 75, c2: 60, ans: 50 }
      ];
      const chosen = prng.pick(cases);

      const correctVal = chosen.ans;
      const distractors = [correctVal - 5, correctVal + 5, correctVal + 10];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `${v}%`,
        text_en: `${v}%`,
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `خُلط ${chosen.v1} لترًا من محلول تركيزه ${chosen.c1}% مع ${chosen.v2} لترًا من محلول آخر لنفس المادة بتركيز ${chosen.c2}%. ما هي النسبة المئوية الدقيقة لتركيز المزيج النهائي الناتج؟`,
        questionText_en: `Mixing ${chosen.v1} liters of a ${chosen.c1}% solution with ${chosen.v2} liters of a ${chosen.c2}% solution yields what final concentration percentage?`,
        options,
        correctIndex,
        explanation_ar: `كمية المادة النقية = (${chosen.v1} × 0.${chosen.c1}) + (${chosen.v2} × 0.${chosen.c2}) = ${(chosen.v1 * chosen.c1 + chosen.v2 * chosen.c2) / 100}. بقسمتها على الحجم الكلي (${chosen.v1 + chosen.v2}) لترًا، ينتج تركيز قدره ${correctVal}% (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Total solute mass divided by total solution volume yields exactly ${correctVal}% (${options[correctIndex].id}).`
      };
    }
  },

  // 15. Alternating Geometric Progression with Sign Inversion
  {
    id: 'quant_geometric_ratio_fraction',
    domain: 'quantitative_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const base = prng.pick([3, 4, 5]);
      const mult = -2;

      const t1 = base;
      const t2 = t1 * mult;
      const t3 = t2 * mult;
      const t4 = t3 * mult;
      const correctVal = t4 * mult;

      const sequence = [t1, t2, t3, t4, '?'];
      const distractors = [-correctVal, correctVal + 8, correctVal - 8];
      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const options = allVals.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(v),
        text_en: String(v),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتضاعف حدود المتتالية الهندسية مع انعكاس دوري للإشارة الرياضية (+ ثم -). ما هو الحد الخامس؟\n\n[ ${sequence.join(' , ')} ]`,
        questionText_en: `Terms in the geometric progression scale with alternating sign inversion (+ / -). What is the fifth term?\n\n[ ${sequence.join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `أساس المتتالية الهندسية هو الضرب في (${mult}). بضرب الحد الرابع (${t4}) في (${mult}) نحصل على: ${t4} × (${mult}) = ${correctVal} (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The common ratio is r = ${mult}. Multiplying ${t4} by ${mult} establishes ${correctVal} (${options[correctIndex].id}).`
      };
    }
  }
];
